import utils, {axiosWithAuth} from '@imt/vue-toolbox/src/utils';
import Vue from 'vue';
import Vuex from 'vuex';

import {generateId} from '@/utilities';

Vue.use(Vuex);

export const LOADING_MESSAGES = [
    'Analyzing your description…',
    'Building expression tree…',
    'Mapping conditions and actions…',
    'Assembling rule structure…',
    'Checking component lookups…',
    'Wiring operators and groups…',
    'Validating rule shape…',
    'Polishing the draft…',
];

const LITERAL_TYPE_MAP = {string: 'single', numeric: 'single'};

function transformComponent(comp, expressionId, parentId = null) {
    const compId = comp.sync_id || generateId(),
        rawData = comp.data || {},
        data = comp.component_type === 'literal' && rawData.dataType && LITERAL_TYPE_MAP[rawData.dataType]
            ? {...rawData, dataType: LITERAL_TYPE_MAP[rawData.dataType]}
            : rawData;

    return {
        id: compId,
        syncId: compId,
        expressionId,
        parentId,
        componentType: comp.component_type,
        position: comp.position,
        data,
        topLevel: !parentId,
        children: (comp.children || []).map(child => transformComponent(child, expressionId, compId)),
    };
}

function transformExpression(expr, groupId) {
    const exprId = expr.sync_id || generateId();

    return {
        id: exprId,
        syncId: exprId,
        groupId,
        operator: expr.operator,
        position: expr.position,
        components: (expr.components || []).map(comp => transformComponent(comp, exprId)),
    };
}

function transformGroup(group, parentId = null) {
    const groupId = group.sync_id || generateId();

    return {
        id: groupId,
        syncId: groupId,
        parentId,
        operator: group.operator,
        position: group.position,
        expressions: (group.expressions || []).map(expr => transformExpression(expr, groupId)),
        children: (group.children || []).map(child => transformGroup(child, groupId)),
    };
}

function transformAction(action) {
    const actionId = action.sync_id || generateId();

    return {
        id: actionId,
        syncId: actionId,
        actionType: action.action_type,
        condition: action.condition,
        target: action.target || '',
        errorMessage: action.error_message || '',
        data: action.data || {},
    };
}

export const getters = {};

export const actions = {
    async searchRules({commit, rootState}, query) {
        commit('SET_AI_GENERATOR_DATA', {field: 'isSearching', data: true});
        try {
            const authAxios = axiosWithAuth(),
                response = await authAxios.get(
                    `/policy-systems/${rootState.currentPolicySystem.id}/rules/`,
                    {params: {'filter[title]': query, 'page[size]': 8, include: 'latest_version'}},
                );
            commit('SET_AI_GENERATOR_DATA', {
                field: 'searchResults',
                data: utils.dataFormatter.deserialize(response.data) || [],
            });
        } catch {
            commit('SET_AI_GENERATOR_DATA', {field: 'searchResults', data: []});
        } finally {
            commit('SET_AI_GENERATOR_DATA', {field: 'isSearching', data: false});
        }
    },
    selectReferenceRule({commit}, rule) {
        commit('SET_AI_GENERATOR_DATA', {field: 'referenceRule', data: rule});
        commit('SET_AI_GENERATOR_DATA', {field: 'ruleSearch', data: ''});
        commit('SET_AI_GENERATOR_DATA', {field: 'searchResults', data: []});
    },
    clearReference({commit}) {
        commit('SET_AI_GENERATOR_DATA', {field: 'referenceRule', data: null});
        commit('SET_AI_GENERATOR_DATA', {field: 'ruleSearch', data: ''});
        commit('SET_AI_GENERATOR_DATA', {field: 'searchResults', data: []});
    },
    setField({commit}, {field, data}) {
        commit('SET_AI_GENERATOR_DATA', {field, data});
    },
    resetSession({commit}) {
        commit('RESET_SESSION');
    },
    handleGenerationResponse({commit}, raw) {
        if (raw.type === 'question') {
            commit('SET_AI_GENERATOR_DATA', {field: 'conversationId', data: raw.conversation_id});
            commit('APPEND_CONVERSATION_ENTRY', {role: 'assistant', text: raw.question});

            return;
        }

        commit('SET_PENDING_AI_DATA', {
            description: raw.description || '',
            transactionType: raw.transaction_type || [],
            states: raw.states || [],
            expressionGroups: (raw.expression_groups || []).map(g => transformGroup(g)),
            actions: (raw.actions || []).map(transformAction),
            warnings: raw.warnings || [],
        });

        commit('SET_AI_GENERATOR_DATA', {field: 'isDone', data: true});
    },
    async generate({commit, dispatch, state, rootState}) {
        commit('SET_AI_GENERATOR_DATA', {field: 'isLoading', data: true});
        commit('SET_AI_GENERATOR_DATA', {field: 'errorMessage', data: null});

        try {
            const attributes = {
                rule_prompt: state.prompt,
                rule_type: state.ruleType,
            };

            if (state.referenceRule) {
                attributes.reference_rule_id = state.referenceRule.id;
            }

            const authAxios = axiosWithAuth(),
                response = await authAxios.post(
                    `/policy-systems/${rootState.currentPolicySystem.id}/ai/generate-rule/`,
                    {data: {type: 'AIGenerateRule', attributes}},
                );

            commit('SET_AI_GENERATOR_DATA', {
                field: 'conversationLog',
                data: [{role: 'user', text: state.prompt}],
            });

            await dispatch('handleGenerationResponse', response.data);
        } catch {
            commit('SET_AI_GENERATOR_DATA', {
                field: 'errorMessage',
                data: 'Generation failed. Please try again.',
            });
        } finally {
            commit('SET_AI_GENERATOR_DATA', {field: 'isLoading', data: false});
        }
    },
    async sendReply({commit, dispatch, state, rootState}) {
        const reply = state.replyText.trim();
        if (!reply) {
            return;
        }

        commit('SET_AI_GENERATOR_DATA', {field: 'isLoading', data: true});
        commit('SET_AI_GENERATOR_DATA', {field: 'errorMessage', data: null});
        commit('APPEND_CONVERSATION_ENTRY', {role: 'user', text: reply});
        commit('SET_AI_GENERATOR_DATA', {field: 'replyText', data: ''});

        try {
            const authAxios = axiosWithAuth(),
                response = await authAxios.post(
                    `/policy-systems/${rootState.currentPolicySystem.id}/ai/generate-rule/`,
                    {
                        data: {
                            type: 'AIGenerateRule',
                            attributes: {
                                conversation_id: state.conversationId,
                                reply,
                            },
                        },
                    },
                );

            await dispatch('handleGenerationResponse', response.data);
        } catch {
            commit('SET_AI_GENERATOR_DATA', {
                field: 'errorMessage',
                data: 'Generation failed. Please try again.',
            });
        } finally {
            commit('SET_AI_GENERATOR_DATA', {field: 'isLoading', data: false});
        }
    },
};

export const mutations = {
    SET_AI_GENERATOR_DATA(state, {field, data}) {
        state[field] = data;
    },
    SET_PENDING_AI_DATA(state, data) {
        state.pendingAIData = data;
    },
    APPEND_CONVERSATION_ENTRY(state, entry) {
        state.conversationLog.push(entry);
    },
    RESET_SESSION(state) {
        const initial = createSessionState();
        Object.keys(initial).forEach(key => {
            state[key] = initial[key];
        });
    },
};

function createSessionState() {
    return {
        ruleType: 'cross_validation',
        prompt: '',
        referenceRule: null,
        ruleSearch: '',
        searchResults: [],
        isSearching: false,
        isLoading: false,
        isDone: false,
        errorMessage: null,
        loadingMessage: LOADING_MESSAGES[0],
        conversationId: null,
        conversationLog: [],
        replyText: '',
    };
}

export const state = () => {
    return {
        ...createSessionState(),
        pendingAIData: null,
    };
};

export default {
    actions,
    mutations,
    getters,
    namespaced: true,
    state: state(),
};
